import { useMemo } from 'react';
import { Profile, Address } from '../lib/api';

interface CompletenessField {
  key: string;
  labelKey: string;
  weight: number;
  required: boolean;
}

const COMPLETENESS_FIELDS: CompletenessField[] = [
  { key: 'firstName', labelKey: 'firstName', weight: 15, required: true },
  { key: 'lastName', labelKey: 'lastName', weight: 15, required: true },
  { key: 'phone', labelKey: 'phone', weight: 15, required: true },
  { key: 'email', labelKey: 'email', weight: 15, required: true },
  { key: 'photoUrl', labelKey: 'photo', weight: 15, required: false },
  { key: 'tagline', labelKey: 'tagline', weight: 10, required: false },
  { key: 'website', labelKey: 'website', weight: 5, required: false },
  { key: 'address', labelKey: 'address', weight: 10, required: false },
];

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  tagline: string;
  website: string;
  address: Address;
}

interface ProfileCompletenessResult {
  percentage: number;
  missingFields: CompletenessField[];
  isComplete: boolean;
}

export function useProfileCompleteness(
  formData: FormData,
  profile: Profile | null
): ProfileCompletenessResult {
  return useMemo(() => {
    let completed = 0;
    const missing: CompletenessField[] = [];

    for (const field of COMPLETENESS_FIELDS) {
      let hasValue = false;

      switch (field.key) {
        case 'firstName':
          hasValue = Boolean(formData.firstName?.trim());
          break;
        case 'lastName':
          hasValue = Boolean(formData.lastName?.trim());
          break;
        case 'phone':
          hasValue = Boolean(formData.phone?.trim());
          break;
        case 'email':
          hasValue = Boolean(formData.email?.trim());
          break;
        case 'photoUrl':
          hasValue = Boolean(profile?.photoUrl);
          break;
        case 'tagline':
          hasValue = Boolean(formData.tagline?.trim());
          break;
        case 'website':
          hasValue = Boolean(formData.website?.trim());
          break;
        case 'address':
          hasValue = Boolean(
            formData.address?.city?.trim() || formData.address?.streetAddress?.trim()
          );
          break;
      }

      if (hasValue) {
        completed += field.weight;
      } else if (!field.required) {
        missing.push(field);
      }
    }

    return {
      percentage: completed,
      missingFields: missing,
      isComplete: completed === 100,
    };
  }, [formData, profile]);
}
